import { useState, useEffect, useCallback, useRef } from "react";
import { useUser } from "../contexts/UserContext";
import translation from "../translations/orderFormTranslation";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { protectedResources } from "../authConfig";
import { readFileInChunks } from "../Utils/serviceFile";
import { isTestEnv } from "../globals";
import { useQuery } from "@tanstack/react-query";
import debounce from "lodash/debounce";

const useOrderForm = (roles, open, contactCodes, setOpen, setFlag, editingOrder = null, duplicatingOrder = null) => {
  const { user } = useUser();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tempCity, setTempCity] = useState("");
  const [openCities, setOpenCities] = useState(false);
  const [hasUserInput, setHasUserInput] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDuplicateMode, setIsDuplicateMode] = useState(false);
  const [formData, setFormData] = useState({
    selectedRecivingCustomer: "",
    selectedOrderingCustomer:
      roles[0]?.properties[0]?.SalesPromotions.ordererCustomer?.length == 1
        ? roles[0].properties[0]?.SalesPromotions.ordererCustomer[0].custname
        : "",
    nsDesCustomer: "",
    selectedProducts: [],
    contractCode: "99",
    address:
      roles[0]?.properties[0]?.SalesPromotions.ordererCustomer?.length == 1
        ? roles[0].properties[0]?.SalesPromotions.ordererCustomer[0].address
        : {},
    deliveryDate: "",
    drivertext: "",
    orderstext: "",
    internalDialogtext: "",
    selectedFiles: [],
    site: "",
    orderType: "",
    email: user.email,
    branch: roles[0]?.properties[0]?.SalesPromotions.branch || "",
  });

  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productsLoadProgress, setProductsLoadProgress] = useState(null); // { loaded, total } | null
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [siteOptions, setSiteOptions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [minDeliveryDate, setMinDeliveryDate] = useState("");
  const [holidays, setHolidays] = useState([]);
  const [selectedRole, setSelectedRole] = useState(roles[0]);
  const [marketingClient, setMarketingClient] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isReadOnlyAddress =
    formData.selectedOrderingCustomer !== ""
      ? !(
          selectedRole?.properties[0]?.SalesPromotions?.ordererCustomer?.find(
            (c) => c.custname === formData.selectedOrderingCustomer
          )?.customerType === translation.external
        ) || !marketingClient
      : false;

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  // tracks PARTNAMEs already loaded/in-flight for live inventory data, so
  // fetchLiveProductData doesn't refetch on every focus/re-render
  const liveDataRequestedRef = useRef(new Set());

  // bumped on every fetchProducts() call so page results from a stale/
  // superseded call (role switched, dialog reopened) are silently dropped
  const productsFetchGenerationRef = useRef(0);

  // when opening the product selector while the background load is still
  // running - pause it briefly (until the browser has time to paint the
  // dialog), so it doesn't compete for the main thread right at the critical
  // moment of opening. Then it resumes exactly where it left off
  const productsFetchPausedRef = useRef(false);
  const waitWhileProductsFetchPaused = async () => {
    while (productsFetchPausedRef.current) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  };
  const pauseProductsFetchBriefly = (ms = 400) => {
    productsFetchPausedRef.current = true;
    setTimeout(() => {
      productsFetchPausedRef.current = false;
    }, ms);
  };

  // fetchProducts() now starts prefetching as soon as the form opens, before
  // formData.selectedProducts (populated from editingOrder/duplicatingOrder
  // in a separate effect) is necessarily ready — mergeSelectedProducts reads
  // this ref instead of the formData closure so every page merged during an
  // in-flight fetch (before or after that effect runs) sees the current value
  const selectedProductsRef = useRef(formData.selectedProducts);
  useEffect(() => {
    selectedProductsRef.current = formData.selectedProducts;
  }, [formData.selectedProducts]);

  // retroactively fixes products already loaded (from prefetch) *before*
  // formData.selectedProducts was populated in edit/duplicate mode - without
  // refetching from the server, just merging locally into what's already there
  useEffect(() => {
    if (!((isEditMode || duplicatingOrder) && formData.selectedProducts.length > 0)) return;
    setProducts((prev) =>
      prev.map((product) => {
        const selectedProduct = formData.selectedProducts.find(
          (sp) => sp.PARTNAME === product.PARTNAME
        );
        return selectedProduct
          ? { ...product, quantity: selectedProduct.quantity, selectedSerial: selectedProduct.selectedSerial }
          : product;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.selectedProducts, isEditMode, duplicatingOrder]);

  useEffect(() => {
    // in edit mode - don't reset site and don't change the address
    if (isEditMode) return;

    setFormData((prevFormData) => ({
      ...prevFormData,
      site: "",
    }));
    // Get selected ordering & receiving customers - searches by both custname and custdes
    const selectedOrderingCustomer =
      selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.find(
        (c) => c.custname === formData.selectedOrderingCustomer ||
               c.custdes === formData.selectedOrderingCustomer
      );

    const selectedReceivingCustomer =
      selectedRole?.properties[0]?.SalesPromotions.receiveCustomer?.find(
        (c) => c.custname === formData.selectedRecivingCustomer ||
               c.custdes === formData.selectedRecivingCustomer
      );

    // If receiving customer exists, use their data
    if (formData.selectedRecivingCustomer && selectedReceivingCustomer) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        orderType:
          selectedReceivingCustomer.customerType === translation.internal
            ? selectedRole.properties[0].SalesPromotions.intOrderType
            : selectedRole.properties[0].SalesPromotions.extOrderType,
        address: selectedReceivingCustomer.address || "",
      }));
    }
    // Otherwise, if ordering customer exists, use their data
    else if (formData.selectedOrderingCustomer && selectedOrderingCustomer) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        orderType:
          selectedOrderingCustomer.customerType === translation.internal
            ? selectedRole.properties[0].SalesPromotions.intOrderType
            : selectedRole.properties[0].SalesPromotions.extOrderType,
        address: selectedOrderingCustomer.address || "",
      }));
    }
  }, [formData.selectedRecivingCustomer, formData.selectedOrderingCustomer, isEditMode]);

  useEffect(() => {
    if (formData.site !== "" && siteOptions.length > 0) {


      const specSite = siteOptions.find(
        (s) => s.code === formData.site.split(" ")[0]
      );
      if (specSite) {
        setFormData({
          ...formData,
          address: specSite.address || {},
          phonenum: specSite.address?.phonenum || "0000000000",
        });
      }
    }
  }, [formData.site, siteOptions]);

  useEffect(() => {
    // in edit or duplicate mode - don't reset the form when the role changes
    if (isEditMode || isDuplicateMode) return;

    const orderingCustomer =
      selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.length === 1
        ? selectedRole.properties[0].SalesPromotions.ordererCustomer[0].custname
        : "";
    console.log(170, "orderingCustomer", orderingCustomer);

    setFormData((prevFormData) => ({
      ...prevFormData,
      selectedRecivingCustomer: "",
      selectedOrderingCustomer: orderingCustomer,
      nsDesCustomer: "",
      selectedProducts: [],
      contractCode: "99",
      address: orderingCustomer
        ? selectedRole.properties[0].SalesPromotions.ordererCustomer[0]
            .address || {}
        : {},
      deliveryDate: "",
      drivertext: "",
      orderstext: "",
      internalDialogtext: "",
      selectedFiles: [],
      site: "",
      email: user.email,
      branch: selectedRole?.properties[0]?.SalesPromotions.branch || "",
    }));
    setProducts([]);
  }, [selectedRole, user.email, isEditMode, isDuplicateMode]);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      let response = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/daysWithoutDelivery/getDaysWithoutDelivery`
      );
      setHolidays(response);
      setIsLoading(false);
    };
    getData();
  }, []);

  useEffect(() => {
    if (holidays.length > 0) {
      const today = new Date();
      const minDate = addWorkingDays(today, 5);
      setMinDeliveryDate(minDate);
    }
  }, [holidays]);

  // load data of an order for editing
  useEffect(() => {
    if (editingOrder && open) {
      setIsEditMode(true);
      // find the matching role
      console.log('DEBUG - roles:', roles);
      console.log('DEBUG - editingOrder.role:', editingOrder.role);
      const orderRole = roles.find(r => r._id === editingOrder.role);
      console.log('DEBUG - orderRole found:', orderRole);
      if (orderRole) {
        setSelectedRole(orderRole);
      }

      // check if this is a marketing customer
      // if selectedRecivingCustomer isn't found in the customer list - it's likely a temporary customer
      const roleToUseForMarketing = orderRole || selectedRole;
      const foundReceivingCustomer = roleToUseForMarketing?.properties[0]?.SalesPromotions.receiveCustomer
        ?.find(c => c.custname === editingOrder.selectedRecivingCustomer ||
                    c.custdes === editingOrder.selectedRecivingCustomer);

      const isMarketingCustomer = editingOrder.selectedRecivingCustomer && (
        foundReceivingCustomer?.nsFlag === "Y" ||
        !foundReceivingCustomer // if not found - it's a temporary customer
      );

      console.log('DEBUG - isMarketingCustomer:', isMarketingCustomer, 'foundReceivingCustomer:', foundReceivingCustomer);
      setMarketingClient(isMarketingCustomer || false);

      // load all data into the form
      // if it's a temporary customer not found in the list - its name is in selectedRecivingCustomer
      const nsDesCustomerValue = isMarketingCustomer && !foundReceivingCustomer
        ? editingOrder.selectedRecivingCustomer
        : editingOrder.nsDesCustomer || "";

      setFormData({
        selectedRecivingCustomer: foundReceivingCustomer ? editingOrder.selectedRecivingCustomer : "",
        selectedOrderingCustomer: editingOrder.selectedOrderingCustomer || "",
        nsDesCustomer: nsDesCustomerValue,
        selectedProducts: editingOrder.selectedProducts || [],
        contractCode: editingOrder.contractCode || "99",
        address: editingOrder.address || {},
        deliveryDate: editingOrder.deliveryDate || "",
        drivertext: editingOrder.drivertext || "",
        orderstext: editingOrder.orderstext || "",
        internalDialogtext: editingOrder.internalDialogtext || "",
        selectedFiles: editingOrder.selectedFiles || [],
        site: editingOrder.site || "",
        orderType: editingOrder.orderType || "",
        email: user.email,
        branch: editingOrder.branch || "",
      });
      console.log(213, editingOrder);

      // load the sites based on the customer
      // in edit mode, the values contain custdes - need to find the custname
      // uses orderRole if it exists, otherwise selectedRole (the current role)
      const roleToUse = orderRole || selectedRole;
      let customerForSites = null;

      // if it's a temporary customer - use the ordering customer to load the sites
      if (isMarketingCustomer && !foundReceivingCustomer) {
        // temporary customer - use the ordering customer
        if (editingOrder.selectedOrderingCustomer) {
          const foundOrderingCustomer = roleToUse?.properties[0]?.SalesPromotions.ordererCustomer?.find(
            c => c.custdes === editingOrder.selectedOrderingCustomer
          );
          customerForSites = foundOrderingCustomer?.custname || editingOrder.selectedOrderingCustomer;
          console.log('DEBUG - using ordering customer for marketing client:', foundOrderingCustomer);
        }
      } else {
        // regular customer - first try with the receiving customer
        if (editingOrder.selectedRecivingCustomer && foundReceivingCustomer) {
          customerForSites = foundReceivingCustomer.custname;
          console.log('DEBUG - using receiving customer:', foundReceivingCustomer);
        }

        // if there's no receiving customer, try with the ordering customer
        if (!customerForSites && editingOrder.selectedOrderingCustomer) {
          const foundOrderingCustomer = roleToUse?.properties[0]?.SalesPromotions.ordererCustomer?.find(
            c => c.custdes === editingOrder.selectedOrderingCustomer
          );
          customerForSites = foundOrderingCustomer?.custname || editingOrder.selectedOrderingCustomer;
          console.log('DEBUG - using ordering customer:', foundOrderingCustomer);
        }
      }

      console.log('DEBUG - final customerForSites:', customerForSites);
      if (customerForSites) {
        const fetchSitesForEdit = async () => {
          setIsLoadingSites(true);
          try {
            const response = await execute(
              "GET",
              `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/getCUSTDESTSOfCustomer?custname=${customerForSites}`
            );
            setSiteOptions(response);
          } catch (error) {
            console.error("Error fetching site options for edit:", error);
          } finally {
            setIsLoadingSites(false);
          }
        };
        fetchSitesForEdit();
      }
    } else if (!open) {
      // reset edit mode when the form is closed
      setIsEditMode(false);
    }
  }, [editingOrder, open, roles]);

  // load products from a duplicated order
  useEffect(() => {
    if (duplicatingOrder && open && !editingOrder) {
      console.log('Duplicating order - loading products only:', duplicatingOrder);
      setIsDuplicateMode(true);

      // copy only the products from the duplicated order
      // all other fields remain empty/default
      const duplicatedProducts = duplicatingOrder.selectedProducts?.map(product => ({
        ...product,
        // reset fields that shouldn't be duplicated
        isOverLimit: false,
      })) || [];

      // check if there's only one ordering customer - if so, select it and its address
      const orderingCustomers = selectedRole?.properties[0]?.SalesPromotions.ordererCustomer;
      const hasOnlyOneCustomer = orderingCustomers?.length === 1;
      const defaultCustomer = hasOnlyOneCustomer ? orderingCustomers[0].custname : "";
      const defaultAddress = hasOnlyOneCustomer ? orderingCustomers[0].address || {} : {};

      setFormData(prevFormData => ({
        ...prevFormData,
        selectedProducts: duplicatedProducts,
        selectedOrderingCustomer: defaultCustomer,
        address: defaultAddress,
      }));
    } else if (!open) {
      // reset duplicate mode when the form is closed
      setIsDuplicateMode(false);
    }
  }, [duplicatingOrder, open, editingOrder, selectedRole]);

  useEffect(() => {
    // checks if the receiving customer is a marketing customer - by both custname and custdes
    const marketingCustomers = selectedRole?.properties[0]?.SalesPromotions.receiveCustomer
      ?.filter((c) => c.nsFlag === "Y");
    const isMarketingByName = marketingCustomers?.some(
      (c) => c.custname === formData.selectedRecivingCustomer ||
             c.custdes === formData.selectedRecivingCustomer
    );

    if (isMarketingByName) {
      setMarketingClient(true);
      // in edit mode, don't reset the address
      if (!isEditMode) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          address: {},
        }));
      }
    }
  }, [formData.selectedRecivingCustomer, isEditMode]);
  /**Functions for calculating a date are available */
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 5 || day === 6; // Friday or Saturday
  };

  const isHoliday = (date) => {
    // Convert input date to local date string (YYYY-MM-DD)
    const localDateString = date.toLocaleString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    if (holidays && holidays.length) {
      return holidays.some((holiday) => {
        // Convert holiday date to Date object in local time
        const holidayDate = new Date(holiday.CURDATE);
        // Convert holiday date to local date string (YYYY-MM-DD)
        const holidayLocalDateString = holidayDate.toLocaleString("en-CA", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });

        return holidayLocalDateString === localDateString;
      });
    }

    return false;
  };

  const isWorkingDay = (date) => {
    return !isWeekend(date) && !isHoliday(date);
  };

  const addWorkingDays = (date, days) => {
    let count = 0;
    let currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() - 1);
    while (count < days) {
      currentDate.setDate(currentDate.getDate() + 1);
      if (isWorkingDay(currentDate)) {
        count++;
      }
    }
    return currentDate;
  };

  const handleAddressChange = async (e) => {
    handleInputChange({
      target: {
        name: "address",
        value: { ...formData.address, [e.target.name]: e.target.value },
      },
    });
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    setAnchorEl(null);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const getCurrentValue = () => {
    if (!formData.site) return null;
 
    // looks for an exact match or a match by code only
    const found = siteOptions.find(
      (site) => `${site.code} ${site.codedes}` === formData.site ||
                site.code === formData.site.split(" ")[0]
    );
    return found || null;
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDateChange = (newValue) => {
    if (newValue) {
      // convert to a local date in ISO format (YYYY-MM-DD)
      const year = newValue.getFullYear();
      const month = String(newValue.getMonth() + 1).padStart(2, "0");
      const day = String(newValue.getDate()).padStart(2, "0");
      const localDateString = `${year}-${month}-${day}`;

      handleInputChange({
        target: {
          name: "deliveryDate",
          value: localDateString,
        },
      });
    } else {
      handleInputChange({
        target: {
          name: "deliveryDate",
          value: "",
        },
      });
    }
  };

  const shouldDisableDate = (date) => {
    return !isWorkingDay(date);
  };
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    if (isSubmitted) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: undefined,
      }));
    }
  };

  const handleFileRemove = (index) => {
    setFormData((prevState) => {
      const fileToRemove = prevState.selectedFiles[index];

      // clean up the preview URL if it exists
      if (fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      const updatedFiles = prevState.selectedFiles.filter(
        (_, i) => i !== index
      );
      return { ...prevState, selectedFiles: updatedFiles };
    });
  };

  const getCustomersInfo = () => {
    const customersInfo = {
      orderingCustomer: null,
      receivingCustomer: null,
    };

    // there's always an ordering customer - searches by both custname and custdes
    const selectedOrderingCustomer =
      selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.find(
        (c) => c.custname === formData.selectedOrderingCustomer ||
               c.custdes === formData.selectedOrderingCustomer
      );
    if (formData.selectedOrderingCustomer && selectedOrderingCustomer) {
      customersInfo.orderingCustomer = {
        name: selectedOrderingCustomer.custname,
        description: selectedOrderingCustomer.custdes,
      };
    }

    // check if there's a receiving customer
    if (marketingClient && formData.nsDesCustomer) {
      // occasional customer
      customersInfo.receivingCustomer = {
        name: formData.nsDesCustomer,
        description: formData.nsDesCustomer,
        isMarketingClient: true,
      };
    } else if (formData.selectedRecivingCustomer) {
      // regular receiving customer - searches by both custname and custdes
      const selectedReceivingCustomer =
        selectedRole?.properties[0]?.SalesPromotions.receiveCustomer?.find(
          (c) => c.custname === formData.selectedRecivingCustomer ||
                 c.custdes === formData.selectedRecivingCustomer
        );
      if (selectedReceivingCustomer) {
        customersInfo.receivingCustomer = {
          name: selectedReceivingCustomer.custname,
          description: selectedReceivingCustomer.custdes,
          isMarketingClient: false,
        };
      }
    }

    return customersInfo;
  };

  const fetchSiteOptions = async () => {
    setIsLoadingSites(true);

    // in edit mode, selectedRecivingCustomer contains custdes - need to find the custname
    let recivingcCstname = formData.selectedRecivingCustomer;
    if (recivingcCstname) {
      const foundCustomer = selectedRole?.properties[0]?.SalesPromotions.receiveCustomer?.find(
        c => c.custdes === recivingcCstname
      );
      if (foundCustomer) {
        recivingcCstname = foundCustomer.custname;
      }
    }

    // in edit mode, selectedOrderingCustomer contains custdes - need to find the custname
    let orderingCstname = formData.selectedOrderingCustomer;
    if (orderingCstname) {
      const foundCustomer = selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.find(
        c => c.custdes === orderingCstname
      );
      if (foundCustomer) {
        orderingCstname = foundCustomer.custname;
      }
    }

    console.log(
      245,
      "orderingCstname, recivingcCstname",
      orderingCstname,
      recivingcCstname
    );

    if (!recivingcCstname && !orderingCstname) {
      setSnackbar({
        open: true,
        message: translation.selectCustomerFirst,
        severity: "warning",
      });
      setIsLoadingSites(false);
      return;
    }
    try {
      let response = await execute(
        "GET",
        `${
          process.env.REACT_APP_BACKEND_BASE_URL
        }/api/salesPromotions/getCUSTDESTSOfCustomer?custname=${
          recivingcCstname || orderingCstname
        }`
      );

      setSiteOptions(response); // Assuming the API returns an array of site options
    } catch (error) {
      isTestEnv && console.error("Error fetching site options:", error);
      setSnackbar({
        open: true,
        message: translation.errorLoadingSites,
        severity: "error",
      });
    } finally {
      setIsLoadingSites(false);
    }
  };

  const handleFileChange = async (event) => {
    try {
      const files = Array.from(event.target.files);

      // ✅ store File objects
      const fileObjects = files.map((file) => ({
        file: file, // the original File object
        filename: file.name,
        contentType: file.type,
        size: file.size,
        preview: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
      }));

      setFormData((prevFormData) => ({
        ...prevFormData,
        selectedFiles: [...prevFormData.selectedFiles, ...fileObjects],
      }));
    } catch (error) {
      isTestEnv && console.error("Error handling files:", error);
      setSnackbar({
        open: true,
        message: "שגיאה בטעינת הקבצים",
        severity: "error",
      });
    } finally {
      event.target.value = null;
    }
  };

  const onClose = () => {
    setMarketingClient(false);
    setSiteOptions([]);
    setFormData({
      selectedRecivingCustomer: "",
      selectedOrderingCustomer: "",
      nsDesCustomer: "",
      selectedProducts: [],
      contractCode: "99",
      shippingAddress:
        roles[0]?.properties[0]?.SalesPromotions.ordererCustomer?.length == 1
          ? roles[0].properties[0]?.SalesPromotions.ordererCustomer[0].address
          : {},
      deliveryDate: "",
      drivertext: "",
      orderstext: "",
      internalDialogtext: "",
      selectedFiles: [],
      site: "",
      orderType: "",
      email: user.email,
      branch: selectedRole?.properties[0]?.SalesPromotions.branch,
    });
    setProducts((products) =>
      products.length
        ? products.map((p) => {
            return { ...p, quantity: 0 };
          })
        : []
    );
    setFormErrors({});
    setIsEditMode(false);
    setOpen(!open);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.selectedOrderingCustomer) {
      errors.selectedOrderingCustomer =
        translation.selectOrderingCustomerRequired;
    }
    if (!formData.nsDesCustomer && marketingClient) {
      errors.nsDesCustomer = translation.selectCustomerNameRequired;
    }
    if (!formData.address.phonenum && marketingClient) {
      errors.phonenum = translation.selectCustomerPhoneRequired;
    }
    if (formData.selectedProducts.length === 0) {
      errors.selectedProducts = translation.selectProductsRequired;
    }
    if (!formData.deliveryDate) {
      errors.deliveryDate = translation.deliveryDateRequired;
    }
    if (!formData.contractCode) {
      errors.contractCode = translation.contractCodeRequired;
    }
    if (
      formData.deliveryDate &&
      !isWorkingDay(new Date(formData.deliveryDate + "T00:00:00"))
    ) {
      errors.deliveryDate = translation.deliveryDateInvalid;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getDeliveryCustomerDescription = () => {
    // 1. if a site is selected - the site name is the customer's description
    if (formData.site !== "") {
      const specSite = siteOptions.find(
        (s) => s.code === formData.site.split(" ")[0]
      );
      return specSite?.codedes || "";
    }

    // 2. if there's a receiving customer - its description
    if (formData.selectedRecivingCustomer) {
      if (marketingClient && formData.nsDesCustomer) {
        // occasional customer - the manually typed name
        return formData.nsDesCustomer;
      } else {
        // regular receiving customer
        const selectedReceivingCustomer =
          selectedRole?.properties[0]?.SalesPromotions.receiveCustomer?.find(
            (c) => c.custname === formData.selectedRecivingCustomer
          );
        return selectedReceivingCustomer?.custdes || "";
      }
    }

    // 3. otherwise - the ordering customer's description
    const selectedOrderingCustomer =
      selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.find(
        (c) => c.custname === formData.selectedOrderingCustomer
      );
    return selectedOrderingCustomer?.custdes || "";
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
  };

  const handlePreviewConfirm = () => {
    handleSubmit(null, false, true); // submit while skipping validation
  };

  const handleSubmit = async (
    event,
    isAfterAlert = false,
    skipValidation = false
  ) => {
    if (!skipValidation && event) {
      event.preventDefault();
    }

    if (!skipValidation) {
      setIsSubmitted(true);
      if (!validateForm()) {
        setSnackbar({
          open: true,
          message: translation.fillRequiredFields,
          severity: "error",
        });
        return;
      }
      setFormData((prevFormData) => ({ ...prevFormData, email: user.email }));
      if (
        !isAfterAlert &&
        formData.selectedProducts.some((p) => p.isOverLimit)
      ) {
        setShowAlert(true);
        return;
      }
      setPreviewOpen(true);
      return;
    }

    try {
      setIsLoading(true);

      // ✅ convert File objects to arrays - no Buffer!
      // only new files are sent - existing files are already on the server
      let processedFiles = [];
      if (formData.selectedFiles && formData.selectedFiles.length > 0) {
        // filter to only the new files (they have a file object)
        const newFiles = formData.selectedFiles.filter(fileObj => fileObj.file);

        if (newFiles.length > 0) {
          processedFiles = await Promise.all(
            newFiles.map(async (fileObj) => {
              const arrayBuffer = await fileObj.file.arrayBuffer();
              const uint8Array = new Uint8Array(arrayBuffer);

              return {
                content: Array.from(uint8Array),
                filename: fileObj.filename,
                contentType: fileObj.contentType,
              };
            })
          );
        }
      }

      // convert custdes to custname in edit mode
      let selectedOrderingCustomerToSend = formData.selectedOrderingCustomer;
      let selectedRecivingCustomerToSend = formData.selectedRecivingCustomer;

      if (isEditMode) {
        // check if selectedOrderingCustomer is a custdes and not a custname
        const orderingCustomerByCustname = selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.find(
          (c) => c.custname === formData.selectedOrderingCustomer
        );
        if (!orderingCustomerByCustname) {
          // if not found by custname, search by custdes
          const orderingCustomerByCustdes = selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.find(
            (c) => c.custdes === formData.selectedOrderingCustomer
          );
          if (orderingCustomerByCustdes) {
            selectedOrderingCustomerToSend = orderingCustomerByCustdes.custname;
          }
        }

        // check if selectedRecivingCustomer is a custdes and not a custname (only if not an occasional customer)
        if (!marketingClient && formData.selectedRecivingCustomer) {
          const receivingCustomerByCustname = selectedRole?.properties[0]?.SalesPromotions.receiveCustomer?.find(
            (c) => c.custname === formData.selectedRecivingCustomer
          );
          if (!receivingCustomerByCustname) {
            // if not found by custname, search by custdes
            const receivingCustomerByCustdes = selectedRole?.properties[0]?.SalesPromotions.receiveCustomer?.find(
              (c) => c.custdes === formData.selectedRecivingCustomer
            );
            if (receivingCustomerByCustdes) {
              selectedRecivingCustomerToSend = receivingCustomerByCustdes.custname;
            }
          }
        }
      }

      // send everything together
      let orderData;

      if (isEditMode) {
        // in edit mode - send only what changed
        const changedFields = {};

        // function to compare products - only by the relevant fields
        const compareProducts = (current, original) => {
          if (!current || !original) return current !== original;
          if (current.length !== original.length) return true;

          // sort by PARTNAME for comparison
          const sortedCurrent = [...current].sort((a, b) => a.PARTNAME?.localeCompare(b.PARTNAME));
          const sortedOriginal = [...original].sort((a, b) => a.PARTNAME?.localeCompare(b.PARTNAME));

          return sortedCurrent.some((prod, idx) =>
            prod.PARTNAME !== sortedOriginal[idx]?.PARTNAME ||
            prod.quantity !== sortedOriginal[idx]?.quantity ||
            prod.selectedSerial !== sortedOriginal[idx]?.selectedSerial
          );
        };

        // function to compare addresses - only the relevant fields
        const compareAddresses = (current, original) => {
          if (!current || !original) return current !== original;
          const relevantFields = ['STATE', 'NEO_STREET', 'NEO_APARTMENT', 'NEO_FLOOR', 'phonenum'];
          return relevantFields.some(field => current[field] !== original[field]);
        };

        // check for changes in the fields
        if (formData.selectedOrderingCustomer !== editingOrder.selectedOrderingCustomer) {
          changedFields.selectedOrderingCustomer = selectedOrderingCustomerToSend;
        }
        if (formData.selectedRecivingCustomer !== editingOrder.selectedRecivingCustomer) {
          changedFields.selectedRecivingCustomer = selectedRecivingCustomerToSend;
        }
        if ((formData.nsDesCustomer || "") !== (editingOrder.nsDesCustomer || "")) {
          changedFields.nsDesCustomer = formData.nsDesCustomer;
        }
        if (compareProducts(formData.selectedProducts, editingOrder.selectedProducts)) {
          changedFields.selectedProducts = formData.selectedProducts;
        }
        if (formData.contractCode !== editingOrder.contractCode) {
          changedFields.contractCode = formData.contractCode;
        }
        if (compareAddresses(formData.address, editingOrder.address)) {
          changedFields.address = formData.address;
        }
        if (formData.deliveryDate !== editingOrder.deliveryDate) {
          changedFields.deliveryDate = formData.deliveryDate;
        }
        if ((formData.drivertext || "") !== (editingOrder.drivertext || "")) {
          changedFields.drivertext = formData.drivertext;
        }
        if ((formData.orderstext || "") !== (editingOrder.orderstext || "")) {
          changedFields.orderstext = formData.orderstext;
        }
        if ((formData.internalDialogtext || "") !== (editingOrder.internalDialogtext || "")) {
          changedFields.internalDialogtext = formData.internalDialogtext;
        }
        if ((formData.site || "") !== (editingOrder.site || "")) {
          changedFields.site = formData.site;
        }
        if (formData.orderType !== editingOrder.orderType) {
          changedFields.orderType = formData.orderType;
        }
        if (formData.branch !== editingOrder.branch) {
          changedFields.branch = formData.branch;
        }

        // new files - always sent if present
        if (processedFiles.length > 0) {
          changedFields.selectedFiles = processedFiles;
        }

        // always send deliveryCustomerDescription if anything relevant changed
        if (changedFields.selectedRecivingCustomer || changedFields.site || changedFields.selectedOrderingCustomer) {
          changedFields.deliveryCustomerDescription = getDeliveryCustomerDescription();
        }

        if (Object.keys(changedFields).length > 0) {
          orderData = await execute(
            "PATCH",
            `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/updateOrder/${editingOrder._id}`,
            changedFields
          );
        }
      } else {
        // new-creation mode
        orderData = await execute(
          "POST",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/createOrder`,
          {
            ...formData,
            selectedFiles: processedFiles,
            selectedOrderingCustomer: selectedOrderingCustomerToSend,
            selectedRecivingCustomer: selectedRecivingCustomerToSend,
            role: selectedRole._id,
            deliveryCustomerDescription: getDeliveryCustomerDescription(),
          }
        );
      }

      // reset the form after a successful save
      setMarketingClient(false);
      setSiteOptions([]);
      setFormData({
        selectedRecivingCustomer: "",
        selectedOrderingCustomer: "",
        nsDesCustomer: "",
        selectedProducts: [],
        contractCode: "99",
        address:
          roles[0]?.properties[0]?.SalesPromotions.ordererCustomer?.length == 1
            ? roles[0].properties[0]?.SalesPromotions.ordererCustomer[0].address
            : {},
        deliveryDate: "",
        drivertext: "",
        orderstext: "",
        internalDialogtext: "",
        selectedFiles: [],
        site: "",
        orderType: "",
        email: user.email,
        branch: selectedRole?.properties[0]?.SalesPromotions.branch || "",
      });
      setProducts((products) => products.map((p) => ({ ...p, quantity: 0 })));

      setTimeout(() => {
        setFlag((prev) => !prev);
        setOpen(false);
      }, 4000);
    } catch (error) {
      console.log(881, error);
      
      isTestEnv && console.error("❌ Error:", error);
      setSnackbar({
        open: true,
        message: error.msg || translation.errorCreatingOrder,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // fetches live inventory/batch for specific PARTNAMEs only (on-demand,
  // never for the whole product list) and merges the result into `products`
  const fetchLiveProductData = async (partNames) => {
    const toFetch = partNames.filter((p) => !liveDataRequestedRef.current.has(p));
    if (!toFetch.length) return;
    toFetch.forEach((p) => liveDataRequestedRef.current.add(p));

    try {
      const liveData = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/getProductLiveData?partNames=${encodeURIComponent(toFetch.join(","))}`
      );
      const liveByPart = new Map((liveData || []).map((item) => [item.PARTNAME, item]));

      setProducts((prevProducts) =>
        prevProducts.map((p) =>
          liveByPart.has(p.PARTNAME)
            ? {
                ...p,
                LOGCOUNTERS_SUBFORM: liveByPart.get(p.PARTNAME).LOGCOUNTERS_SUBFORM,
                PARTBALANCE_SUBFORM: liveByPart.get(p.PARTNAME).PARTBALANCE_SUBFORM,
                liveDataLoaded: true,
              }
            : p
        )
      );
    } catch (error) {
      isTestEnv && console.error("Error fetching live product data:", error);
      toFetch.forEach((p) => liveDataRequestedRef.current.delete(p));
    }
  };

  const FIRST_PRODUCTS_PAGE_SIZE = 100;
  const REST_PRODUCTS_PAGE_SIZE = 150;
  const PRODUCTS_PAGE_CONCURRENCY = 3;

  // edit/duplicate - merges the quantity/batch already selected in the order into
  // the products page. Reads from the ref (not directly from formData) so it
  // doesn't "lock in" a stale value from when the fetch started - see the
  // comment next to selectedProductsRef.
  const mergeSelectedProducts = (items) => {
    const selectedProducts = selectedProductsRef.current;
    if (!((isEditMode || duplicatingOrder) && selectedProducts.length > 0)) {
      return items;
    }
    return items.map((product) => {
      const selectedProduct = selectedProducts.find(
        (sp) => sp.PARTNAME === product.PARTNAME
      );
      return selectedProduct
        ? { ...product, quantity: selectedProduct.quantity, selectedSerial: selectedProduct.selectedSerial }
        : { ...product, quantity: 0 };
    });
  };

  const PRODUCTS_PAGE_TIMEOUT_MS = 30000;

  // without a timeout, a page request that hangs (e.g. an MSAL token issue)
  // could stay pending forever without failing or succeeding - and the
  // batch's Promise.all would never resolve, leaving the progress bar
  // "stuck" at the last percentage that succeeded, with no visible error and
  // no continuation of the load. Now it fails visibly after 30 seconds,
  // shows an error message, and can be retried.
  const fetchProductsPage = async (skip, limit) =>
    Promise.race([
      execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/getProductByRole?role_id=${selectedRole._id}&skip=${skip}&limit=${limit}`
      ),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Timed out loading products page (skip=${skip})`)), PRODUCTS_PAGE_TIMEOUT_MS)
      ),
    ]);

  const fetchProducts = async () => {
    const generation = ++productsFetchGenerationRef.current;
    const isCurrent = () => generation === productsFetchGenerationRef.current;

    setIsLoadingProducts(true);
    setProductsLoadProgress(null);
    try {
      const firstPage = await fetchProductsPage(0, FIRST_PRODUCTS_PAGE_SIZE);
      if (!isCurrent()) return;

      setProducts(mergeSelectedProducts(firstPage.items));
      setIsLoadingProducts(false);

      const { total } = firstPage;
      let loaded = firstPage.items.length;
      if (loaded >= total) {
        setProductsLoadProgress(null);
        return;
      }
      setProductsLoadProgress({ loaded, total });

      const remainingSkips = [];
      for (let skip = loaded; skip < total; skip += REST_PRODUCTS_PAGE_SIZE) {
        remainingSkips.push(skip);
      }

      // each page that comes back is added to a buffer, but is only shown in
      // the table (setProducts) at a limited rate (no more than once per
      // PRODUCTS_FLUSH_INTERVAL_MS) - a table of almost 1000 rows re-renders
      // on every state update, and if that happens for every single page
      // (every few dozen ms) the screen freezes and can't be scrolled during
      // the load. The progress indicator still shows real numbers at every step.
      const PRODUCTS_FLUSH_INTERVAL_MS = 400;
      let pendingItems = [];
      let lastFlushAt = 0;

      const flushPending = () => {
        if (!pendingItems.length || !isCurrent()) return;
        const toAdd = pendingItems;
        pendingItems = [];
        setProducts((prev) => [...prev, ...toAdd]);
        lastFlushAt = Date.now();
      };

      for (let i = 0; i < remainingSkips.length; i += PRODUCTS_PAGE_CONCURRENCY) {
        if (!isCurrent()) return;
        await waitWhileProductsFetchPaused();
        if (!isCurrent()) return;
        const batch = remainingSkips.slice(i, i + PRODUCTS_PAGE_CONCURRENCY);
        await Promise.all(
          batch.map(async (skip) => {
            const page = await fetchProductsPage(skip, REST_PRODUCTS_PAGE_SIZE);
            if (!isCurrent()) return;
            pendingItems.push(...mergeSelectedProducts(page.items));
            loaded += page.items.length;
            setProductsLoadProgress({ loaded, total });
            if (Date.now() - lastFlushAt >= PRODUCTS_FLUSH_INTERVAL_MS) {
              flushPending();
            }
          })
        );
      }
      flushPending();
      if (isCurrent()) setProductsLoadProgress(null);
    } catch (error) {
      if (!isCurrent()) return;
      isTestEnv && console.error("Error fetching products options:", error);
      setSnackbar({
        open: true,
        message: translation.errorLoadingProducts,
        severity: "error",
      });
      setIsLoadingProducts(false);
      setProductsLoadProgress(null);
    }
  };

  // as soon as the order form itself opens (even before clicking "edit order
  // products") - start loading the products in the background, so by the
  // time the user actually clicks the button they're already loaded (or
  // loading) and won't feel any slowness. In edit/duplicate mode,
  // handleOpenProductSelector still re-fetches on click so the quantities
  // loaded from formData.selectedProducts are correct - this is just an
  // early prefetch.
  useEffect(() => {
    if (open && selectedRole && !products.length) {
      fetchProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedRole]);

  const handleOpenProductSelector = () => {
    // always opens immediately - if products are already loading/loaded
    // (prefetch since the form opened), continue that same load instead of
    // starting over and blocking the opening
    if (!products.length) {
      fetchProducts();
    } else if (productsLoadProgress) {
      // the load is still continuing in the background - pause it briefly so
      // it doesn't compete with the dialog opening itself for the main
      // thread, then resume exactly where it left off
      pauseProductsFetchBriefly();
    }
    setProductSelectorOpen(true);
  };

  const handleCloseProductSelector = () => {
    setProductSelectorOpen(false);
  };

  const handleProductsChange = (newSelectedProducts) => {
    console.log(505, newSelectedProducts);

    setProducts(
      products.map((p) => {
        const matchedProduct = newSelectedProducts.find(
          (np) => np.PARTNAME === p.PARTNAME
        );
        return matchedProduct ? matchedProduct : p;
      })
    );

    setFormData((prevFormData) => ({
      ...prevFormData,
      selectedProducts: newSelectedProducts,
    }));
  };

  const getCities = async (searchText) => {
    const cities = await execute(
      "GET",
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/citiesList/getCityiesByName?cityName=${searchText}`
    );
    return cities;
  };

  const { data: options = [], isFetching } = useQuery({
    queryKey: ["cities", tempCity],
    queryFn: () => getCities(tempCity),
    enabled: marketingClient && tempCity?.length >= 2 && hasUserInput,
    staleTime: Infinity,
    onError: () => {
      setSnackbar({
        open: true,
        message: translation.errorLoadingProducts,
        severity: "error",
      });
    },
  });

  const debouncedInputChange = useCallback(
    debounce((newValue) => {
      setTempCity(newValue);
    }, 400),
    []
  );

  const handleAutoCompleteChange = (event, newValue) => {
    handleInputChange({
      target: {
        name: "site",
        value: newValue ? `${newValue.code} ${newValue.codedes}` : "",
      },
    });
  };

  return {
    holidays,
    onClose,
    translation,
    selectedRole,
    anchorEl,
    handleClose,
    handleSubmit,
    isSubmitted,
    formErrors,
    handleInputChange,
    formData,
    setFormData,
    marketingClient,
    setMarketingClient,
    openCities,
    setOpenCities,
    hasUserInput,
    setHasUserInput,
    showAlert,
    setShowAlert,
    isLoadingSites,
    siteOptions,
    handleAddressChange,
    isReadOnlyAddress,
    tempCity,
    snackbar,
    handleCloseSnackbar,
    products,
    productsLoadProgress,
    fetchLiveProductData,
    debouncedInputChange,
    handleOpenProductSelector,
    fetchSiteOptions,
    handleRoleChange,
    handleClick,
    minDeliveryDate,
    shouldDisableDate,
    handleDateChange,
    isFetching,
    options,
    isLoading, // this is the isLoading that needs to be passed to OrderPreview
    isLoadingProducts,
    handleCloseProductSelector,
    productSelectorOpen,
    handleProductsChange,
    handleFileRemove,
    handleFileChange,
    getCurrentValue,
    handleAutoCompleteChange,
    getCustomersInfo,
    previewOpen,
    handlePreviewClose,
    handlePreviewConfirm,
    isEditMode,
    isDuplicateMode,
  };
};

export default useOrderForm;
